// Zuletzt geöffnete Leads (Befehlspalette, S02). Seit Runde 4 im Konto: der Server merkt sich je Nutzer die letzten Leads
// samt zuletzt angesehenem Kontakt (`GET/PUT /api/zuletzt`, API.md 19.4), so gilt die Liste an jedem Rechner und in der
// Desktop-App. Der Browser hält nur einen Zwischenspeicher (localStorage), damit die Palette sofort etwas zeigt und ohne
// Verbindung nicht leer ist. Aufrufer: `Lead.tsx` ruft `zuletztMerken`, die Befehlspalette liest über `useZuletzt`.
import { useEffect, useState } from "react";
import { zuletztApi } from "../api/zuletzt";

export interface ZuletztLead {
  id: string;
  name: string;
  status_label: string | null;
  /** zuletzt angesehener Kontakt (Name grau hinter dem Lead, ID für den Sprung auf diesen Kontakt) */
  kontakt_id?: string | null;
  kontakt?: string | null;
}

const SCHLUESSEL = "crm-zuletzt";
const MAX = 8;

let vomServer: ZuletztLead[] | null = null;
const hoerer = new Set<() => void>();

function lokalLesen(): ZuletztLead[] {
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    const liste = roh ? (JSON.parse(roh) as ZuletztLead[]) : [];
    return Array.isArray(liste) ? liste.slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function lokalSchreiben(liste: ZuletztLead[]): void {
  try {
    localStorage.setItem(SCHLUESSEL, JSON.stringify(liste.slice(0, MAX)));
  } catch {
    /* privat/gesperrt: nicht merken */
  }
}

function melden(): void {
  hoerer.forEach((h) => h());
}

/** Die Liste, wie sie gerade bekannt ist (Server-Stand, sonst der Zwischenspeicher dieses Browsers). */
export function zuletztLesen(): ZuletztLead[] {
  return (vomServer ?? lokalLesen()).slice(0, MAX);
}

/** Kontakt aus der Adresse (`/lead/…?kontakt=cont_…`): so kommt man aus der Suche auf den Lead. */
function kontaktAusAdresse(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("kontakt");
  } catch {
    return null;
  }
}

/** Lead als eben geöffnet merken: sofort in der Liste, im Hintergrund im Konto. */
export function zuletztMerken(l: ZuletztLead, kontaktId?: string | null): void {
  const kontakt = kontaktId ?? l.kontakt_id ?? kontaktAusAdresse();
  const bisher = zuletztLesen().find((x) => x.id === l.id);
  // Name des Kontakts: den der Aufrufer kennt (Lead-Seite), sonst den schon gemerkten zum selben Kontakt
  const kontaktName = kontakt ? (l.kontakt_id === kontakt && l.kontakt ? l.kontakt : bisher?.kontakt_id === kontakt ? bisher.kontakt : null) : null;
  const eintrag: ZuletztLead = { ...l, kontakt_id: kontakt, kontakt: kontaktName };
  const neu = [eintrag, ...zuletztLesen().filter((x) => x.id !== l.id)].slice(0, MAX);
  if (vomServer) vomServer = neu;
  lokalSchreiben(neu);
  melden();
  zuletztApi.merken(l.id, kontakt).catch(() => undefined); // ohne Verbindung bleibt der Zwischenspeicher
}

/** Ein Kontakt des Leads wurde angesehen (Klick auf den Namen in der Kontaktkarte): als „zuletzt angesehener Kontakt“ merken. */
export function zuletztKontaktMerken(leadId: string, kontaktId: string, kontaktName: string | null): void {
  const bisher = zuletztLesen().find((x) => x.id === leadId);
  if (!bisher) return;
  const neu = [{ ...bisher, kontakt_id: kontaktId, kontakt: kontaktName }, ...zuletztLesen().filter((x) => x.id !== leadId)].slice(0, MAX);
  if (vomServer) vomServer = neu;
  lokalSchreiben(neu);
  melden();
  zuletztApi.merken(leadId, kontaktId).catch(() => undefined);
}

/** Liste vom Server holen und merken. */
export async function zuletztLaden(): Promise<ZuletztLead[]> {
  try {
    const r = await zuletztApi.liste(MAX);
    vomServer = r.data.map((e) => ({ id: e.lead_id, name: e.name, status_label: e.status_label, kontakt_id: e.contact_id, kontakt: e.contact_name }));
    lokalSchreiben(vomServer);
    melden();
  } catch {
    /* Zwischenspeicher bleibt */
  }
  return zuletztLesen();
}

/** Für die Befehlspalette: sofort der bekannte Stand, beim Öffnen frisch vom Server. */
export function useZuletzt(aktiv: boolean): ZuletztLead[] {
  const [liste, setListe] = useState<ZuletztLead[]>(zuletztLesen);
  useEffect(() => {
    const h = () => setListe(zuletztLesen());
    hoerer.add(h);
    return () => {
      hoerer.delete(h);
    };
  }, []);
  useEffect(() => {
    if (aktiv) void zuletztLaden();
  }, [aktiv]);
  return liste;
}
